import React, { useEffect, Fragment, useState, useCallback } from 'react';
import moment from 'moment';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { HasExternalId } from 'selectors/ecsSelectors';
import { getWebSocketConnected, getWebSocketConnecting } from 'selectors/websocketSelectors';
import { BuildingAddress } from 'ui/building/settings/address/buildingAddress/BuildingAddress';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { NoneProvided } from 'ui/shared/noneProvided';
import { PageHeader } from 'ui/shared/pageHeader';
import { VideoCallRecordSet } from 'ui/video/list/videoCallRecordSet/VideoCallRecordSet';
import { ElevatorDeviceList } from '../elevatorDeviceList/ElevatorDeviceList';
import { ElevatorNameField } from '../elevatorNameField/ElevatorNameField';
import { ExternalIdField } from '../externalIdField/ExternalIdField';
import { ThrottledButton } from '../throttledButton/ThrottledButton';
import { useElevator } from '../useElevator/useElevator';
import { ErrorMessage } from 'ui/shared/messages';
import { DebugLink } from 'ui/shared/DebugLink';

require('./ElevatorPage.css');

const minWidth = {
    minWidth: '720px',
};

export const ElevatorPage = () => {
    const { t } = useTranslation();
    const routeMatch = useRouteMatch<HasExternalId>("/elevator/:externalId");
    const externalId = routeMatch && routeMatch.params && routeMatch.params.externalId;
    const { search } = useLocation<HasExternalId>();

    const { hasPermission } = useSession();
    
    const match = /\baction=(.*?)(?:&|$)/i.exec(search || '');
    const action = match && match[1].toLowerCase();

    const {
        saving, loading, loadingImage, imageData, imageTimestamp, imageError, externalId: actualExternalId,
        building, devices, group, pendingOrder, activeOrder, finishedOrder, errorMessage, triggerResetDisplayName, ecsProperties,
        load, changeName, changeExternalId, refreshImage, requestCall, detachElevator, unload,
    } = useElevator();

    const buildingId = building && building.id;

    const websocketConnected = useSelector(getWebSocketConnected);
    const websocketConnecting = useSelector(getWebSocketConnecting);

    const [ elevatorName, setElevatorName ] = useState(group ? group.displayName : '');

    //
    // Update our internal editing value of the elevator name whenever the group changes
    //
    useEffect(() => {
        if (group) {
            setElevatorName(group.displayName);
        }
    }, [ group, setElevatorName ]);

    //
    // Load the data for the external ID whenever it changes
    //
    useEffect(() => {
        if (externalId) {
            void load(externalId, action);
        }
        return () => {
            unload();
        };
    }, [ externalId, action ]);

    const externalIdIsUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(externalId || '');

    const {
        factory = false,
        allowUnsolicitedCalls = false,
        // TODO: disableSensitiveData = false,
    } = ecsProperties || {};

    return (
        <div id="elevator-area" style={ minWidth }>
            <PageHeader
                title={ !building ? '' : loading ? t('LOADING') : building.displayName }
                subTitle={ loading ? '' : <BuildingAddress building={ building }/> }
            />
            <div id="page-content">
                <div id="elevator-page" className="page">
                    <div id="elevator-page-building-header">
                        <Link to={ `/buildings/${building ? building.urlSlug : ''}/elevators` }>
                            { t('ELEVATOR.ELEVATORS_LINK') }
                        </Link>
                    </div>
                    <div id="elevator-page-header">
                        <div id="elevator-page-image">
                            <div id="elevator-page-image-host">
                                { imageData
                                    ? <img
                                        src={ `data:image/jpeg;base64,${imageData}` }
                                    />
                                    : <div className="no-image">
                                        { t('ELEVATOR.NO_IMAGE') }
                                    </div>
                                }
                            </div>
                            <div id="elevator-page-image-details">
                                <div id="elevator-page-image-date">
                                    { imageTimestamp ? moment.utc(imageTimestamp).format('LLL') : '' }
                                </div>
                                { /* TODO: !disableSensitiveData &&
                                <div id="elevator-page-image-commands">
                                    <ThrottledButton
                                        className="ui tiny icon button"
                                        id="elevator-page-image-refresh"
                                        disabled={ loading || loadingImage }
                                        throttleMs={ 5000 }
                                    >
                                        { t('COMMAND.REFRESH') }
                                    </ThrottledButton>
                                    { loadingImage &&
                                        <LoadingSpinner
                                            id="elevator-page-refresh-spinner"
                                            size="tiny"
                                        />
                                    }
                                </div>
                                */ }
                            </div>
                        </div>

                        <div id="elevator-page-details">
                            <div id="elevator-page-name">
                                <div className="label">
                                    { t('ELEVATOR.LABEL.ELEVATOR') }
                                </div>
                                <div className="value">
                                    <ElevatorNameField
                                        saving={ saving }
                                        buildingId={ buildingId }
                                        value={ elevatorName }
                                        onChange={ changeName }
                                        resetElevatorName={ triggerResetDisplayName }
                                    />
                                </div>
                            </div>

                            <div id="elevator-page-externalid">
                                <div className="label">
                                    { t('ELEVATOR.LABEL.EXTERNAL_ID') }
                                </div>
                                <div className="value">
                                    <ExternalIdField
                                        saving={ saving }
                                        buildingId={ buildingId }
                                        externalId={ actualExternalId || '' }
                                        changeExternalId={ changeExternalId }
                                    />
                                </div>
                            </div>

                            { (factory || (allowUnsolicitedCalls && hasPermission(buildingId, Permission.AnswerCall))) &&
                                <button
                                    id="elevator-page-start-call"
                                    type="button"
                                    className="ui red primary button"
                                    onClick={ requestCall }
                                    disabled={ loading || !websocketConnected }
                                >
                                    { t('COMMAND.START_CALL') }
                                </button>
                            }

                            { factory &&
                                <button
                                    id="elevator-page-detach-elevator"
                                    type="button"
                                    className="ui red primary button"
                                    onClick={ detachElevator }
                                    disabled={ loading }
                                >
                                    { t('COMMAND.DETACH_ELEVATOR') }
                                </button>
                            }
                            
                            <ErrorMessage message={ errorMessage }/>
                        </div>

                        <div id="elevator-page-devices">
                            <div className="label">
                                { t('ELEVATOR.LABEL.DEVICES') }
                            </div>
                            <ElevatorDeviceList
                                devices={ devices || [] }
                            />
                        </div>
                    </div>

                    { /* TODO: hasPermission(buildingId, Permission.ViewCalls) &&
                    <div id="elevator-page-calls">
                        <header className="list-name">
                            <h2>
                                { t('VIDEO.LIST.INCOMING') }
                            </h2>
                        </header>
                        <VideoCallRecordSet
                            type="pending"
                            callIds={ pendingOrder }
                        />
                        
                        <header className="list-name">
                            <h2>
                                { t('VIDEO.LIST.ACTIVE') }
                            </h2>
                        </header>
                        <VideoCallRecordSet
                            type="active"
                            callIds={ activeOrder }
                        />

                        <header className="list-name">
                            <h2>
                                { t('VIDEO.LIST.RECENT') }
                            </h2>
                        </header>
                        <VideoCallRecordSet
                            type="recent"
                            callIds={ finishedOrder }
                        />
                    </div>
                    */ }

                    { group &&
                        <DebugLink
                            sql={
                                `--[Elevator]\n` +
                                `SELECT * FROM device_groups WHERE building_id = '${buildingId}' AND id = '${group.id}';\n\n` +
                                `--[External IDs]\n` +
                                `SELECT e.*, o.display_name org_name FROM ecs_external_ids e, orgs o WHERE e.group_id = '${group.id}' AND e.org_id = o.id;\n\n` +
                                `--[Devices]\n` +
                                `SELECT d.id, d.device_class, d.display_name, d.mac_address, s.*, d.properties\nFROM ecs_device_status s,\n    devices d\nWHERE d.building_id = '${buildingId}'\n    AND d.group_id = '${group.id}'\n    AND s.device_id = d.id;\n\n` +
                                `--[Last 10 Calls]\n` +
                                `SELECT e.id, call_id, source, jpeg_width, jpeg_height, e.created, joined, COALESCE(joined, ended) - e.created wait_time, ended, ended - joined call_duration, u.display_name user_name, o.display_name org_name FROM ecs_calls e LEFT JOIN users u ON e.user_id = u.id LEFT JOIN orgs o ON u.org_id = o.id WHERE elevator_id = '${group.id}' ORDER BY created DESC LIMIT 10;\n\n`
                            }
                        />
                    }
                </div>
            </div>
        </div>
    );
};
